/**
 * The platform's Vega-Lite chart: the one place anything touches react-vega.
 *
 * ``react-vega`` 8 is a rewrite of 7 and earlier: the ``<VegaLite>`` /
 * ``<Vega>`` components are gone, and the package now exposes a single
 * ``<VegaEmbed>`` (plus the ``useVegaEmbed`` hook behind it) that hands a spec
 * straight to ``vega-embed``. There is no ``data`` prop any more, which is why
 * callers pass a spec with its rows already inlined. The hook deep-compares the
 * spec and re-embeds on a real change, finalising the previous view — so no
 * remount key is wanted here, and adding one would throw that diffing away.
 *
 * Everything is imported from ``react-vega`` and nothing from ``vega-embed``,
 * ``vega`` or ``vega-lite`` directly: those are react-vega's peers, its business
 * rather than ours, and the spec type is reachable through its own
 * ``VegaEmbedProps``.
 *
 * Specs are authored as plain JSON and cast once, here. Vega-Lite's generated
 * types model the whole grammar as one deep union that an inline literal
 * resolves against for no real safety — a mistyped channel produces pages of
 * unhelpful errors rather than a useful one. The Vega-Lite compiler is what
 * actually catches those, at render.
 */

import clsx from 'clsx';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { VegaEmbed, type VegaEmbedProps } from 'react-vega';
import { usePostHog } from 'src/lib/posthog';

import { VEGA_CONFIG, VEGA_LITE_SCHEMA } from './vegaTheme';
import './vegaChart.css';

/** A Vega or Vega-Lite spec as a caller authors it — plain JSON, cast at the boundary below. */
export type ChartSpec = Record<string, unknown>;

/** The language a spec is written in, which decides how ``vega-embed`` reads it. */
export type ChartGrammar = 'vega-lite' | 'vega';

/** Only ever read as a string, to tell ``vega-embed`` which grammar a spec is in. */
const SCHEMA_BY_GRAMMAR: Readonly<Record<ChartGrammar, string>> = {
  'vega-lite': VEGA_LITE_SCHEMA,
  vega: 'https://vega.github.io/schema/vega/v6.json'
};

function asString(value: unknown): string | undefined {
  return typeof value === 'string' ? value : undefined;
}

/** The spec type ``<VegaEmbed>`` accepts, taken from react-vega's own props. */
type EmbeddableSpec = VegaEmbedProps['spec'];

/** Embed options, so the shared JSON config can be asserted as Vega's ``Config``. */
type ChartEmbedOptions = NonNullable<VegaEmbedProps['options']>;

type EmbedResult = NonNullable<Parameters<NonNullable<VegaEmbedProps['onEmbed']>>[0]>;

interface VegaChartProps {
  /** Vega-Lite JSON, or a spec built in code. */
  spec: string | ChartSpec;
  /**
   * Defaults to Vega-Lite whatever the spec's own ``$schema`` says. Pass
   * ``'vega'`` only for a spec built in code from first-party data, never for
   * one an LLM or the server authored: full Vega's signals and expressions
   * give a spec far more reach into the page than Vega-Lite does.
   */
  grammar?: ChartGrammar;
  /** Names the figure for assistive technology, which cannot read the marks. */
  ariaLabel?: string;
  className?: string;
  /**
   * Named dataset on the live view. When set with {@link values}, later rows
   * are patched in rather than tearing the embed down — so a chart can animate
   * as filters change.
   */
  dataName?: string;
  values?: readonly Record<string, unknown>[];
  /** Custom HTML tooltip renderer (vega-tooltip `formatTooltip`). */
  formatTooltip?: (value: unknown, valueToHtml: (value: unknown) => string, maxDepth: number) => string;
  /**
   * The figure as an SVG document after each successful draw, or `''` when a
   * draw fails. The chat card uses this for expand and download.
   */
  onSvgChange?: (svg: string) => void;
}

function refuseExternalResource(): Promise<never> {
  return Promise.reject(new Error('Charts may not load external resources'));
}

/**
 * Chart specs can be LLM-authored, and Vega fetches whatever a ``data.url``,
 * image mark or link names. Every chart inlines its rows, so the loader refuses
 * all URLs rather than allowlisting any.
 */
const NO_EXTERNAL_LOADER = {
  load: refuseExternalResource,
  sanitize: refuseExternalResource,
  http: refuseExternalResource,
  file: refuseExternalResource
};

/**
 * Module scope, so the reference is stable across renders and the embed hook's
 * deep compare never sees the options change.
 */
const EMBED_OPTIONS = {
  // No export/source/editor menu: "Open in Vega Editor" would hand a
  // third-party site whatever the chart is plotting.
  actions: false as const,
  // SVG rather than canvas, so labels stay crisp when a page is printed from the
  // browser and the marks reach the accessibility tree.
  renderer: 'svg' as const,
  tooltip: true,
  // A spec can name a URL, don't fetch it.
  loader: NO_EXTERNAL_LOADER,
  // Interpret spec expressions instead of compiling them to JavaScript.
  ast: true,
  // The JSON import widens literals such as ``anchor: "start"`` to ``string``.
  config: VEGA_CONFIG as ChartEmbedOptions['config']
};

export default function VegaChart(props: VegaChartProps) {
  const posthog = usePostHog();
  const { spec: specProp } = props;

  const parsed = useMemo((): ChartSpec | Error => {
    if (typeof specProp !== 'string') return specProp;
    try {
      return JSON.parse(specProp) as ChartSpec;
    } catch (error) {
      return error as Error;
    }
  }, [specProp]);

  useEffect(() => {
    if (!(parsed instanceof Error)) return;
    console.error('Vega spec parse error:', parsed);
    posthog.captureException(parsed);
  }, [parsed, posthog]);

  if (parsed instanceof Error) {
    return <div className="text-misc-error py-2 text-center text-sm">Failed to render chart</div>;
  }
  return <VegaChartView {...props} spec={parsed} />;
}

/**
 * Render one Vega-Lite (or, for a first-party spec, Vega) figure, sized to its container.
 *
 * A failed render leaves a stated absence rather than an empty box: a chart that
 * silently draws nothing is indistinguishable from having nothing to show, and
 * on a reporting surface those must not look alike.
 */
function VegaChartView({
  spec,
  grammar = 'vega-lite',
  ariaLabel,
  className,
  dataName,
  values,
  formatTooltip,
  onSvgChange
}: Omit<VegaChartProps, 'spec'> & { spec: ChartSpec }) {
  const posthog = usePostHog();
  const [failed, setFailed] = useState(false);
  const resultRef = useRef<EmbedResult | null>(null);
  const onSvgChangeRef = useRef(onSvgChange);
  onSvgChangeRef.current = onSvgChange;
  const label = ariaLabel ?? asString(spec.title) ?? asString(spec.description) ?? 'Chart';

  // Vega writes the spec's ``description`` onto the container as its
  // ``aria-label`` after mount, replacing the one set below.
  //
  // vega-embed picks the grammar from ``$schema`` over the ``mode`` option, so
  // it is overwritten to keep a spec from opting into full Vega. ``usermeta``
  // is dropped because its ``embedOptions`` override the options below
  // (including the loader, and a JSON patch of the compiled spec).
  const embedSpec = useMemo(() => {
    const { usermeta: _usermeta, ...authored } = spec;
    const base = { ...authored, $schema: SCHEMA_BY_GRAMMAR[grammar], description: label };
    if (!dataName) return base;
    return { ...base, data: { name: dataName, values: [] } };
  }, [spec, grammar, dataName, label]);

  const applyValues = useCallback(
    (result: EmbedResult) => {
      if (!dataName || values === undefined) return;
      result.view.data(dataName, [...values]);
      result.view.run();
    },
    [dataName, values]
  );

  // ``config`` applies to both grammars, as Vega ignores the keys it does not use.
  const options = useMemo(
    () => ({
      ...EMBED_OPTIONS,
      mode: grammar,
      tooltip: formatTooltip ? { formatTooltip } : EMBED_OPTIONS.tooltip
    }),
    [grammar, formatTooltip]
  );

  const publishSvg = useCallback((result: EmbedResult) => {
    if (!onSvgChangeRef.current) return;
    void result.view.toSVG().then(
      (svg) => {
        onSvgChangeRef.current?.(svg);
      },
      () => {
        onSvgChangeRef.current?.('');
      }
    );
  }, []);

  const handleError = useCallback(
    (error: unknown) => {
      console.error('Vega rendering error:', error);
      posthog.captureException(error);
      setFailed(true);
      onSvgChangeRef.current?.('');
    },
    [posthog]
  );
  const handleEmbed = useCallback(
    (result: EmbedResult) => {
      resultRef.current = result;
      setFailed(false);
      applyValues(result);
      publishSvg(result);
    },
    [applyValues, publishSvg]
  );

  useEffect(() => {
    if (!resultRef.current) return;
    applyValues(resultRef.current);
    publishSvg(resultRef.current);
  }, [applyValues, publishSvg]);

  // The embed stays mounted through a failure rather than being swapped out for
  // the message. Unmounting it would strand the reader on the error: the hook
  // that re-embeds lives inside VegaEmbed, so with it gone no later spec would
  // ever be tried. A failed render leaves the container empty, so it collapses
  // and the message is what shows, while the container keeps its width for
  // `width: 'container'` to measure.
  //
  // It does leave the accessibility tree, though. An empty container still
  // carrying `role="img"` and the figure's label would announce a chart that is
  // not there — and announce it beside the message saying it failed to render,
  // which is worse than either alone. So while it is failed it is hidden and
  // unlabelled, and the message is the only thing describing the state.
  return (
    <>
      {failed && <div className="text-misc-error py-2 text-center text-sm">Failed to render chart</div>}
      <VegaEmbed
        // ``lining-nums`` requests Raleway's lining figures, which the face sets
        // in old-style by default: a Vega-Lite spec cannot ask for the feature,
        // but the SVG text inherits it from here.
        className={clsx('block w-full min-w-0 lining-nums', className)}
        role={failed ? undefined : 'img'}
        aria-label={failed ? undefined : label}
        aria-hidden={failed || undefined}
        spec={embedSpec as unknown as EmbeddableSpec}
        options={options}
        onEmbed={handleEmbed}
        onError={handleError}
      />
    </>
  );
}
